import Phaser from 'phaser';
import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import { isSleeping } from '@/domain/sleep';
import type { AppStore } from '@/infra/store';
import { buildCreatureRenderer, type CreatureRendererHandle } from '../render/CreatureRenderer';
import { buildHouseScene, preloadHouseAssets, type BiomeHandle } from '../render/BiomeRenderer';
import { getHouseStageView, type HouseStageInput } from '../render/house-stage';
import { ambientMoveDurationMs, pointDistance } from './house-ambient-motion';

export class HouseScene extends Phaser.Scene {
  static readonly KEY = 'HouseScene';

  private creatureRenderer?: CreatureRendererHandle;
  private biome?: BiomeHandle;
  private storeUnsub?: () => void;
  private busUnsubs: Array<() => void> = [];
  private ambientTween: Phaser.Tweens.Tween | undefined;
  private ambientTimer: Phaser.Time.TimerEvent | undefined;
  private ambientMotionEnabled = false;
  private ambientPausedForReaction = false;
  private ambientPoint = 0;
  private alive = true;

  constructor() {
    super({ key: HouseScene.KEY });
  }

  preload(): void {
    preloadHouseAssets(this);
  }

  create(): void {
    this.alive = true;
    const c = useStore.getState().creature;
    if (!c) {
      logger.warn('house-scene.no-creature');
      return;
    }
    const reducedMotion = useStore.getState().settings.accessibility.reducedMotion;
    this.biome = buildHouseScene(this, { reducedMotion });
    const initialStageInput = toHouseStageInput(useStore.getState());
    this.biome.update(initialStageInput);
    this.creatureRenderer = buildCreatureRenderer(this, c, {
      lightOn: initialStageInput.lightOn,
      onReactionEnd: () => this.resumeAmbientAfterReaction(),
      onReactionStart: () => this.pauseAmbientForReaction(),
      reducedMotion,
      scale: 1.6,
    });
    this.configureAmbientMotion(initialStageInput);

    this.storeUnsub = useStore.subscribe((s) => {
      if (!this.alive) return;
      if (this.creatureRenderer) this.creatureRenderer.setLightOn(s.lightOn);
      if (s.creature && this.creatureRenderer) this.creatureRenderer.update(s.creature);
      if (s.creature && this.biome) {
        const input = toHouseStageInput(s);
        this.biome.update(input);
        this.configureAmbientMotion(input);
      }
      if (s.inWorld) {
        this.alive = false;
        this.scene.stop(HouseScene.KEY);
        this.scene.start('WorldScene');
      }
    });

    // Reactions ao EventBus
    const r = this.creatureRenderer;
    this.busUnsubs.push(eventBus.on('creature.fed', (payload) => this.alive && r?.reactFeed(payload.foodId)));
    this.busUnsubs.push(eventBus.on('creature.played', (payload) => this.alive && r?.reactPlay(payload.toyId)));
    this.busUnsubs.push(eventBus.on('creature.cleaned', () => this.alive && r?.reactClean()));
    this.busUnsubs.push(eventBus.on('creature.healed', () => this.alive && r?.reactHeal()));
    this.busUnsubs.push(eventBus.on('creature.refused', (payload) => {
      if (!this.alive) return;
      if (payload.action === 'discipline' && payload.reason === 'not-needed') r?.reactDiscipline(false);
      else r?.reactRefuse(payload.severity, payload);
    }));
    this.busUnsubs.push(eventBus.on('creature.disciplined', (payload) => this.alive && r?.reactDiscipline(payload.correct)));
    this.busUnsubs.push(eventBus.on('creature.evolved', (payload) => this.alive && r?.reactEvolve(payload.tier)));
    this.busUnsubs.push(eventBus.on('creature.hungry', () => this.alive && r?.reactNeed('feed')));
    this.busUnsubs.push(eventBus.on('creature.unhappy', () => this.alive && r?.reactNeed('play')));
    this.busUnsubs.push(eventBus.on('creature.poop-spawned', () => this.alive && r?.reactNeed('clean')));
    this.busUnsubs.push(eventBus.on('creature.sick-start', () => this.alive && r?.reactNeed('heal')));

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false;
      this.storeUnsub?.();
      this.busUnsubs.forEach((u) => u());
      this.busUnsubs = [];
      this.stopAmbientMotion();
      this.creatureRenderer?.destroy();
      this.biome?.destroy();
    });
  }

  private configureAmbientMotion(input: HouseStageInput): void {
    const enabled = getHouseStageView(input).allowAmbientMotion;
    if (enabled === this.ambientMotionEnabled) return;

    this.ambientMotionEnabled = enabled;
    if (enabled) {
      this.scheduleAmbientMove(900);
    } else {
      this.ambientPausedForReaction = false;
      this.stopAmbientMotion();
      this.creatureRenderer?.setStagePosition(...this.getCreatureHomePosition());
    }
  }

  private scheduleAmbientMove(delay: number): void {
    if (!this.ambientMotionEnabled || this.ambientPausedForReaction || !this.creatureRenderer) return;
    this.ambientTimer?.remove();
    this.ambientTimer = this.time.addEvent({
      delay,
      callback: () => this.moveAmbientCreature(),
    });
  }

  private moveAmbientCreature(): void {
    if (!this.ambientMotionEnabled || this.ambientPausedForReaction || !this.creatureRenderer) return;
    const [homeX, homeY] = this.getCreatureHomePosition();
    const roamX = Math.min(220, Math.max(120, this.scale.width * 0.12));
    const points: Array<[number, number]> = [
      [homeX - roamX * 0.65, homeY + 6],
      [homeX + roamX * 0.5, homeY - 5],
      [homeX - roamX * 0.25, homeY + 10],
      [homeX + roamX, homeY + 3],
      [homeX, homeY - 8],
      [homeX - roamX, homeY + 2],
    ];
    const [x, y] = points[this.ambientPoint % points.length]!;
    this.ambientPoint += 1;

    this.ambientTween?.stop();
    const distance = pointDistance(this.creatureRenderer.container.x, this.creatureRenderer.container.y, x, y);
    this.creatureRenderer.setFacing(x < this.creatureRenderer.container.x ? 1 : -1);
    this.creatureRenderer.setWalking(true);
    this.ambientTween = this.tweens.add({
      targets: this.creatureRenderer.container,
      x,
      y,
      duration: ambientMoveDurationMs(distance),
      ease: 'Sine.InOut',
      onComplete: () => {
        this.creatureRenderer?.setWalking(false);
        this.ambientTween = undefined;
        this.scheduleAmbientMove(1200);
      },
    });
  }

  private stopAmbientMotion(): void {
    this.ambientTimer?.remove();
    this.ambientTimer = undefined;
    this.ambientTween?.stop();
    this.ambientTween = undefined;
    this.creatureRenderer?.setWalking(false);
  }

  private pauseAmbientForReaction(): void {
    if (!this.ambientMotionEnabled) return;
    this.ambientPausedForReaction = true;
    this.stopAmbientMotion();
  }

  private resumeAmbientAfterReaction(): void {
    if (!this.ambientMotionEnabled) return;
    this.ambientPausedForReaction = false;
    this.scheduleAmbientMove(700);
  }

  private getCreatureHomePosition(): [number, number] {
    const floorY = Math.floor(this.scale.height * 0.74);
    return [this.scale.width / 2, floorY - 60];
  }
}

function toHouseStageInput(state: AppStore): HouseStageInput {
  const creature = state.creature;
  const stage = creature?.lifecycle.stage ?? 'dead';
  const sleeping = stage !== 'egg' && stage !== 'dead' && isSleeping(Date.now());

  return {
    happiness: creature?.state.happiness ?? 0,
    lightOn: state.lightOn,
    poopCount: creature?.state.poopCount ?? 0,
    reducedMotion: state.settings.accessibility.reducedMotion,
    sick: creature?.state.sick ?? false,
    sleeping,
    stage,
    tantrum: creature?.state.tantrum ?? false,
  };
}
